import { useState, useCallback, useMemo, useRef, useEffect, lazy, Suspense } from 'react';
import { Outlet, useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Drawer } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import CreateWheel from '@/components/CreateWheel';
import ErrorBoundary from '@/components/ErrorBoundary';
import PageFallback from '@/components/PageFallback';
/*
 * Static, unlike its neighbours above. IconRail and Topbar already import this
 * one eagerly and both are in the shell, so a lazy() here buys nothing — Vite
 * says as much ("dynamically imported but also statically imported … will not
 * move module into another chunk") and the indirection would only suggest a
 * saving that is not there.
 */
import CreateBusinessDialog from '@/components/CreateBusinessDialog';
import IconRail, { RAIL_COLLAPSED, RAIL_EXPANDED } from '@/components/layout/IconRail';
import Sidebar, { SIDEBAR_WIDTH } from '@/components/layout/Sidebar';
import Topbar from '@/components/layout/Topbar';
/*
 * Both composers are lazy, and both are MOUNTED only while open.
 *
 * AppLayout is statically imported by the router, so it can never be split —
 * anything it imports lands in the always-loaded shell. These two dragged in
 * the whole editing stack behind them (MediaUploader -> ImageEditorDialog ->
 * react-easy-crop + emoji-picker-react, and the entire campaign wizard), which
 * measured at 360 kB raw / 104 kB gzip on every page load, including /login.
 */
const PostEditDialog = lazy(() => import('@/components/PostEditDialog'));
const CampaignBuilderDialog = lazy(() => import('@/components/CampaignBuilderDialog'));
/*
 * The rest of the create menu's dialogs, lazy for the same reason: AppLayout is
 * statically imported by the router, so anything it imports eagerly lands in
 * the shell every visitor downloads — including the one who only ever opens
 * /login. Each of these is mounted only while open, so the chunk is not even
 * requested until somebody picks that spoke.
 */
const AutomationBuilderDialog = lazy(() => import('@/components/AutomationBuilderDialog'));
const CreateLandingSiteDialog = lazy(() => import('@/pages/landing/CreateLandingSiteDialog'));
/*
 * Lazy and mounted-only-while-open like the rest, but this one earns it twice
 * over: it is the only component in the app that touches getUserMedia and
 * MediaRecorder, and mounting it on every page would keep that machinery — and
 * the browser's microphone affordance — one render away from a user who never
 * asked for it.
 */
const VoiceAgentDialog = lazy(() => import('@/components/VoiceAgentDialog'));

import OnboardingTour from '@/components/OnboardingTour';
import { useBusinessStore } from '@/store/businessStore';

const RAIL_KEY = 'm_rail_expanded';

/**
 * Pages configure the TopBar and an optional right panel through the outlet context:
 *   const { setTopBar, setRightPanel } = useAppLayout();
 *   useEffect(() => { setTopBar({ title: 'Posts' }); return () => setTopBar(null); }, []);
 */
export function useAppLayout() {
  return useOutletContext();
}

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentBusinessId = useBusinessStore((s) => s.currentBusinessId);

  // Switching the current business from any nav should update the page on the
  // right. List/overview pages re-scope automatically (their queries key off the
  // selected business). Entity-detail pages (a specific package/automation/post/
  // business that belongs to the *previous* business) would go stale — so send
  // the user to the dashboard, which reflects the newly selected business.
  const prevBiz = useRef(currentBusinessId);
  useEffect(() => {
    if (prevBiz.current === currentBusinessId) return;
    prevBiz.current = currentBusinessId;
    const isEntityDetail = /^\/(businesses|automations|packages|posts)\/[^/]+/.test(location.pathname);
    if (isEntityDetail) navigate('/dashboard');
  }, [currentBusinessId, location.pathname, navigate]);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [topBar, setTopBar] = useState(null);
  const [rightPanel, setRightPanel] = useState(null);

  // Global "New post" (create from any page)
  const queryClient = useQueryClient();
  const [newPostOpen, setNewPostOpen] = useState(false);
  // Stable identity for the composer's `post` prop — see the render below.
  const newPost = useMemo(
    () => ({ business_id: currentBusinessId || undefined, status: 'draft' }),
    [currentBusinessId]
  );
  const [multiOpen, setMultiOpen] = useState(false);
  // null when closed, otherwise the automation kind the spoke asked for.
  const [automationKind, setAutomationKind] = useState(null);
  const [landingOpen, setLandingOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  /*
   * The create menu.
   *
   * Colours are lifted from the sidebar rows these actions produce — an orange
   * spoke makes an automation, and Automations is orange in the nav. It costs
   * nothing and it means the wheel teaches the nav rather than competing with
   * it.
   *
   * Ordered by how often they are wanted, not by importance: index 0 sits
   * straight above the trigger, which is the one place you can reach without
   * aiming.
   */
  const createActions = useMemo(() => [
    { key: 'post', label: 'New post', icon: EditOutlinedIcon, color: '#3D5AFE', onClick: () => setNewPostOpen(true) },
    { key: 'package', label: 'AI package (multiple posts)', icon: DynamicFeedOutlinedIcon, color: '#8B5CF6', onClick: () => setMultiOpen(true) },
    { key: 'automation', label: 'Automation · generated posts', icon: BoltOutlinedIcon, color: '#F97316', onClick: () => setAutomationKind('default') },
    { key: 'rss', label: 'Automation · from an RSS feed', icon: RssFeedIcon, color: '#EA580C', onClick: () => setAutomationKind('rss') },
    /*
     * Media navigates instead of opening a dialog, and that is deliberate.
     * There is no upload dialog — the library owns the whole flow (progress,
     * per-file retry, business scoping) behind a hidden file input. Opening
     * that picker from here would mean a second copy of the upload code, and
     * triggering it after a route change is blocked by the browser anyway:
     * the gesture has to be the click on the input itself.
     */
    { key: 'media', label: 'Upload media', icon: PermMediaOutlinedIcon, color: '#F59E0B', onClick: () => navigate('/media') },
    { key: 'landing', label: 'New landing page', icon: WebOutlinedIcon, color: '#F43F5E', onClick: () => setLandingOpen(true) },
    { key: 'business', label: 'New business', icon: StorefrontOutlinedIcon, color: '#14B8A6', onClick: () => setBusinessOpen(true) },
  ], [navigate]);

  // Reset page-provided slots synchronously on navigation so stale config from a
  // previous page does not linger. Done during render (not an effect) so it runs
  // BEFORE the new page's mount effect sets its own title.
  const lastPath = useRef(location.pathname);
  if (lastPath.current !== location.pathname) {
    lastPath.current = location.pathname;
    if (topBar !== null) setTopBar(null);
    if (rightPanel !== null) setRightPanel(null);
  }

  const toggleRail = useCallback(() => {
    setRailExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(RAIL_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const ctx = useMemo(() => ({ setTopBar, setRightPanel }), []);

  const railWidth = railExpanded ? RAIL_EXPANDED : RAIL_COLLAPSED;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Icon rail (desktop fixed) */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: railWidth,
          flexShrink: 0,
          height: '100vh',
          position: 'sticky',
          top: 0,
        }}
      >
        <IconRail expanded={railExpanded} onToggle={toggleRail} />
      </Box>

      {/* Secondary nav (desktop fixed) */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          height: '100vh',
          position: 'sticky',
          top: 0,
        }}
      >
        <Sidebar />
      </Box>

      {/* Mobile drawer: rail + secondary nav stacked */}
      <Drawer
        variant="temporary"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: RAIL_COLLAPSED + SIDEBAR_WIDTH,
            border: 'none',
            display: 'flex',
            flexDirection: 'row',
          },
        }}
      >
        <Box sx={{ width: RAIL_COLLAPSED, height: '100%' }}>
          <IconRail
            expanded={false}
            onToggle={() => {}}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </Box>
        <Box sx={{ flex: 1, height: '100%' }}>
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </Box>
      </Drawer>

      {/* Main column */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar
          onMenuClick={() => setMobileNavOpen(true)}
          title={topBar?.title}
          titleIcon={topBar?.titleIcon}
          view={topBar?.view}
          onViewChange={topBar?.onViewChange}
        />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            gap: { xs: 2, md: 3 },
            p: { xs: 2, md: 3 },
            pt: { xs: 1, md: 1.5 },
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          <Box component="main" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/*
              The page slot has its own error and loading boundaries, so a page
              that is downloading or has crashed affects only this area — the
              rail, sidebar and top bar stay put, like a router outlet in
              Angular. Without them, the nearest boundaries were the ones
              around the whole app, and both a first visit to a page and a
              crash replaced the navigation too.

              The error boundary resets on navigation, so a crash on one page
              does not follow the user to the next.
            */}
            <ErrorBoundary resetKey={location.pathname}>
              <Suspense fallback={<PageFallback />}>
                <Outlet context={ctx} />
              </Suspense>
            </ErrorBoundary>
          </Box>

          {rightPanel && (
            <Box
              component="aside"
              sx={{
                width: { xs: '100%', lg: 320 },
                flexShrink: 0,
                position: { lg: 'sticky' },
                top: { lg: 88 },
              }}
            >
              {rightPanel}
            </Box>
          )}
        </Box>
      </Box>

      {/* Floating create menu — available on every page */}
      <CreateWheel
        actions={createActions}
        /*
         * Not one of `actions`, deliberately. The seven spokes each open a form
         * you then fill in; this one does the filling in. Same wheel, different
         * kind of thing — so it gets its own ring, its own size and the only
         * gradient on the screen.
         */
        centerpiece={{
          key: 'voice',
          label: 'AI voice — just say what you want',
          icon: GraphicEqRoundedIcon,
          gradient: 'linear-gradient(135deg, #3D5AFE, #8B5CF6 55%, #EC4899)',
          onClick: () => setVoiceOpen(true),
        }}
      />

      {/* `newPost` is held in state, not rebuilt inline. As a fresh object
          literal it changed identity on every AppLayout render, and the
          composer's [post] effect reset the caption, media and tags — wiping
          what the user had typed whenever anything else on the page updated. */}
      <Suspense fallback={null}>
        {newPostOpen && (
          <PostEditDialog
            post={newPost}
            onClose={() => setNewPostOpen(false)}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['posts'] });
              queryClient.invalidateQueries({ queryKey: ['calendar-posts'] });
            }}
          />
        )}

        {multiOpen && (
          <CampaignBuilderDialog
            open
            onClose={() => {
              setMultiOpen(false);
              queryClient.invalidateQueries({ queryKey: ['posts'] });
              queryClient.invalidateQueries({ queryKey: ['calendar-posts'] });
            }}
          />
        )}

        {automationKind && (
          <AutomationBuilderDialog
            open
            initialType={automationKind}
            onClose={() => setAutomationKind(null)}
            onCreated={() => {
              queryClient.invalidateQueries({ queryKey: ['automations'] });
              queryClient.invalidateQueries({ queryKey: ['posts'] });
            }}
          />
        )}

        {landingOpen && (
          <CreateLandingSiteDialog
            open
            onClose={() => setLandingOpen(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['landing-sites'] })}
          />
        )}

        {businessOpen && (
          <CreateBusinessDialog
            open
            onClose={() => setBusinessOpen(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['businesses'] })}
          />
        )}

        {voiceOpen && (
          <VoiceAgentDialog open onClose={() => setVoiceOpen(false)} />
        )}
      </Suspense>

      {/* First-run checklist. Uncontrolled here: it decides for itself whether
          to open, once per account, based on live progress from the server. */}
      <OnboardingTour />
    </Box>
  );
}
